import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useRedux';
import Logo from './Logo';
import TeslaButton from './TeslaButton';
import './MainNavigation.css';

interface MainNavigationProps {
  variant?: 'transparent' | 'solid';
  className?: string;
}

const MainNavigation: React.FC<MainNavigationProps> = ({ 
  variant = 'transparent',
  className = ''
}) => {
  const navigate = useNavigate();
  const { token, user } = useAppSelector((state) => state.auth);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle scroll effect for transparency only
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Set scrolled state for transparency
      setIsScrolled(currentScrollY > 50);
      
      // Debug logging
      console.log('Scroll position:', currentScrollY, 'isScrolled:', currentScrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetStarted = () => {
    if (token && user) {
      // Check if email is verified
      if (user.emailVerified !== true) {
        navigate('/verify-email', {
          state: {
            email: user.email,
            name: user.name
          }
        });
        return;
      }
      // Redirect to appropriate dashboard based on user type
      const userType = user.userType || user.role;
      if (userType === 'client') {
        navigate('/client/dashboard');
      } else if (userType === 'stylist') {
        navigate('/stylist/dashboard');
      } else {
        navigate('/dashboard');
      }
    } else {
      navigate('/register');
    }
  };

  const handleSignIn = () => {
    if (token && user) {
      // Already signed in, check email verification
      if (user.emailVerified !== true) {
        navigate('/verify-email', {
          state: {
            email: user.email,
            name: user.name
          }
        });
      } else {
        handleGetStarted();
      }
    } else {
      navigate('/login');
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const navClasses = [
    'main-nav',
    variant === 'transparent' ? 'main-nav--transparent' : 'main-nav--solid',
    isScrolled && variant === 'transparent' ? 'main-nav--scrolled' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <nav className={navClasses}>
      <div className="main-nav__container">
        {/* Logo */}
        <div className="main-nav__logo">
          <Logo size="medium" homePath="/home" />
        </div>

        {/* Desktop Navigation Links */}
        <div className="main-nav__links">
          <Link to="/browse" className="main-nav__link">
            Find a Stylist
          </Link>
          <Link to="/services" className="main-nav__link">
            Services
          </Link>
          <Link to="/home#how-it-works" className="main-nav__link">
            How It Works
          </Link>
          <Link to="/about" className="main-nav__link">
            About
          </Link>
          <Link to="/contact" className="main-nav__link">
            Contact
          </Link>
        </div>

        {/* Desktop Actions */}
        <div className="main-nav__actions">
          {token && user && user.emailVerified === true ? (
            <div className="main-nav__user">
              <span className="main-nav__user-name">
                Welcome, {user.name?.split(' ')[0] || 'User'}
              </span>
              <TeslaButton 
                variant="primary" 
                size="sm"
                onClick={handleGetStarted}
              >
                Dashboard
              </TeslaButton>
            </div>
          ) : (
            <>
              <button 
                className="main-nav__sign-in"
                onClick={handleSignIn}
              >
                Sign In
              </button>
              <TeslaButton 
                variant="primary" 
                size="sm"
                onClick={() => navigate('/become-a-stylist')}
              >
                Become a Stylist
              </TeslaButton>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button 
          className={`main-nav__mobile-toggle ${isMobileMenuOpen ? 'main-nav__mobile-toggle--open' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`main-nav__mobile-menu ${isMobileMenuOpen ? 'main-nav__mobile-menu--open' : ''}`}>
        <div className="main-nav__mobile-links">
          <Link
            to="/browse"
            className="main-nav__mobile-link"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Find a Stylist
          </Link>
          <Link 
            to="/services" 
            className="main-nav__mobile-link"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Services
          </Link>
          <Link 
            to="/home#how-it-works" 
            className="main-nav__mobile-link"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            How It Works
          </Link>
          <Link 
            to="/about" 
            className="main-nav__mobile-link"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            About
          </Link>
          <Link 
            to="/contact" 
            className="main-nav__mobile-link"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Contact
          </Link>
        </div>
        
        <div className="main-nav__mobile-actions">
          {token && user && user.emailVerified === true ? (
            <div className="main-nav__mobile-user">
              <span className="main-nav__mobile-user-name">
                Welcome, {user.name?.split(' ')[0] || 'User'}
              </span>
              <TeslaButton 
                variant="primary" 
                size="md"
                onClick={() => {
                  handleGetStarted();
                  setIsMobileMenuOpen(false);
                }}
              >
                Dashboard
              </TeslaButton>
            </div>
          ) : (
            <>
              <button 
                className="main-nav__mobile-sign-in"
                onClick={() => {
                  handleSignIn();
                  setIsMobileMenuOpen(false);
                }}
              >
                Sign In
              </button>
              <TeslaButton 
                variant="primary" 
                size="md"
                onClick={() => {
                  navigate('/become-a-stylist');
                  setIsMobileMenuOpen(false);
                }}
              >
                Become a Stylist
              </TeslaButton>
            </>
          )}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="main-nav__overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </nav>
  );
};

export default MainNavigation; 